import { useState } from 'react';

import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import { Address } from 'types/graphql';

import { ControlledCheckBox } from 'src/components/CheckBox/ControlledCheckBox';
import { InputBaseField } from 'src/components/common/form/FormInput';
import CountrySelectInput from 'src/components/CountrySelectInput/CountrySelectInput';
import { InputWithSearch } from 'src/components/InputWithSearch';
import Loader from 'src/components/Loader/Loader';
import MainCard from 'src/components/MainCard/MainCard';
import { AddressListModal } from 'src/components/Modals';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useAddressLookUp } from 'src/hooks/useAddressLookup';
import useCountryHelpers from 'src/hooks/useCountryHelpers';
import { isIreland } from 'src/lib/countryConfig';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';
import { supportedCountries } from 'src/utils/utils';

export type OpenModalState = {
    open: boolean;
    addressForm?: string;
};

export type AddressList = {
    id: string;
    text: string;
};

export const AddressInformation = ({ values, setValues, setFieldValue }) => {
    const { merchantApplication } = useAddMerchantContext();
    const { getPostalLabel, getCountryName } = useCountryHelpers();
    const postalLabel = getPostalLabel(merchantApplication?.partner?.countryDetails?.alpha2Code);

    // Modal state management
    const [openAddressModal, setOpenAddressModal] = useState<OpenModalState>({
        open: false,
    });

    // Address data
    const { addresses, findAddress, searchingForAddress } = useAddressLookUp();

    // State used by the controlled checkbox
    const [sameAsRegisteredAddress, setSameAsRegisteredAddress] = useState(false);
    const [formattedRegisteredAddress, setFormattedRegisteredAddress] = useState<string | null>(
        null
    );
    const [formattedTradingAddress, setFormattedTradingAddress] = useState<string | null>(null);
    // Submit handler for the search button
    const onFindAddress = async (value, context) => {
        findAddress(value, () => {
            return setOpenAddressModal({ open: true, addressForm: context });
        });
    };

    // Change event handler for the controlled checkbox
    const toggleChecked = (event) => {
        const { checked } = event.target;

        setSameAsRegisteredAddress(checked);

        if (checked) {
            const regAddress = values?.aboutBusiness?.registeredAddress;

            setValues({
                ...values,
                outlets: [
                    {
                        ...values.outlets[0],
                        tradingAddress: {
                            eircode: regAddress.eircode,
                            addressLine1: regAddress.addressLine1,
                            addressLine2: regAddress.addressLine2,
                            townOrCity: regAddress.townOrCity,
                            country: regAddress.country,
                            county: regAddress.county,
                        },
                    },
                ],
            });
        } else {
            setValues({
                ...values,
                outlets: [
                    {
                        ...values.outlets[0],
                        tradingAddress: {
                            eircode: '',
                            addressLine1: '',
                            addressLine2: '',
                            townOrCity: '',
                            country: getCountryName(
                                merchantApplication?.partner?.countryDetails?.alpha2Code
                            ),
                            county: '',
                        },
                    },
                ],
            });
        }
    };

    const onRetrievalOfCountry = (address: Address, formattedAddress: string) => {
        if (openAddressModal.addressForm === 'registeredAddressSearch') {
            setValues((prev) => {
                return {
                    ...prev,
                    aboutBusiness: {
                        ...prev?.aboutBusiness,
                        registeredAddress: {
                            ...prev?.aboutBusiness?.registeredAddress,
                            addressLine1: address.line1,
                            addressLine2: address.line2,
                            townOrCity: address.city,
                            county: address.province,
                            country: address.countryName,
                            eircode: address.postalCode,
                        },
                    },
                };
            });
            setFormattedRegisteredAddress(formattedAddress);
        } else {
            setValues((prev) => {
                return {
                    ...prev,
                    outlets: [
                        {
                            ...prev.outlets[0],
                            tradingAddress: {
                                ...prev.outlets[0].tradingAddress,
                                addressLine1: address.line1,
                                addressLine2: address.line2,
                                townOrCity: address.city,
                                county: address.province,
                                country: address.countryName,
                                eircode: address.postalCode,
                            },
                        },
                    ],
                };
            });
            setFormattedTradingAddress(formattedAddress);
        }
    };

    const isEditingDisabled = isApplicationEditable(merchantApplication);

    return (
        <>
            <MainCard>
                {searchingForAddress && <Loader />}
                <Typography fontSize="24px" color="#1D1D1B" marginBottom="8px">
                    Address Information
                </Typography>
                <Divider
                    sx={{
                        borderColor: 'warning.main',
                        marginBottom: '34px',
                    }}
                />
                <Typography fontSize="20px" color="#F18F34" marginBottom="32px">
                    Registered Address
                </Typography>

                <Box className="mt-20">
                    <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                        {/* Row 1 */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item width="100%">
                                    <Grid item xs={12} md={12}>
                                        <InputWithSearch
                                            searchText="Search"
                                            onSearch={onFindAddress}
                                            placeholder="Search"
                                            label="Search"
                                            fieldName="registeredAddressSearch"
                                            maskedValue={formattedRegisteredAddress}
                                            clearMaskedValue={() => {
                                                return setFormattedRegisteredAddress(null);
                                            }}
                                            disabled={isEditingDisabled}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                        {/* Row 2 */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="Address Line 1"
                                        name="aboutBusiness.registeredAddress.addressLine1"
                                        placeholder="Address Line 1"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="Address Line 2"
                                        name="aboutBusiness.registeredAddress.addressLine2"
                                        placeholder="Address Line 2"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        {/* Row 3 */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="Town/City"
                                        name="aboutBusiness.registeredAddress.townOrCity"
                                        placeholder="Enter Town/City"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="County"
                                        name="aboutBusiness.registeredAddress.county"
                                        placeholder="Enter County"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        {/* Row 4 */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <CountrySelectInput
                                        setFieldValue={setFieldValue}
                                        fieldName="aboutBusiness.registeredAddress.country"
                                        filterByAlpha2Codes={supportedCountries(isIreland)}
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label={postalLabel}
                                        name="aboutBusiness.registeredAddress.eircode"
                                        placeholder={postalLabel}
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Stack>
                </Box>

                <Grid
                    container
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    marginTop="43px"
                    marginBottom="33px"
                >
                    <Typography fontSize="20px" color="#F18F34">
                        Trading Address
                    </Typography>
                    <Box>
                        <ControlledCheckBox
                            heading="Same as Registered Address"
                            value={sameAsRegisteredAddress}
                            name="sameAsRegisteredAddress"
                            label="Same as Registered Address"
                            showValue={false}
                            handleChange={toggleChecked}
                            disabled={isEditingDisabled}
                        />
                    </Box>
                </Grid>

                <Box className="mt-20">
                    <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                        {/* Row 5 */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item width="100%">
                                    <Grid item xs={12} md={12}>
                                        <InputWithSearch
                                            searchText="Search"
                                            onSearch={onFindAddress}
                                            placeholder="Search"
                                            label="Search"
                                            fieldName="tradingSearch"
                                            maskedValue={formattedTradingAddress}
                                            clearMaskedValue={() => {
                                                return setFormattedTradingAddress(null);
                                            }}
                                            disabled={isEditingDisabled}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                        {/* Row 6 */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="Address Line 1"
                                        name="outlets[0].tradingAddress.addressLine1"
                                        placeholder="Address Line 1"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="Address Line 2"
                                        name="outlets[0].tradingAddress.addressLine2"
                                        placeholder="Address Line 2"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        {/* Row 7 */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="Town/City"
                                        name="outlets[0].tradingAddress.townOrCity"
                                        placeholder="Enter Town/City"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="County"
                                        name="outlets[0].tradingAddress.county"
                                        placeholder="Enter County"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        {/* Row 8 */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <CountrySelectInput
                                        setFieldValue={setFieldValue}
                                        fieldName="outlets[0].tradingAddress.country"
                                        filterByAlpha2Codes={supportedCountries(isIreland)}
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label={postalLabel}
                                        name="outlets[0].tradingAddress.eircode"
                                        placeholder={postalLabel}
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Stack>
                </Box>
            </MainCard>

            <AddressListModal
                isOpen={openAddressModal.open}
                onClose={() => {
                    return setOpenAddressModal((prev) => {
                        return { ...prev, open: false };
                    });
                }}
                addressList={addresses}
                onSelectedAddress={onRetrievalOfCountry}
            />
        </>
    );
};
